import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router";
// @mui
import {
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import Chart from "react-apexcharts";

// components
import { useSettingsContext } from "../components/settings";

// Redux

// routes
import Iconify from "../components/iconify";
import { useSnackbar } from "../components/snackbar";
import { PATH_AUTH, PATH_DASHBOARD } from "../routes/paths";

// sections
import { AppWidgetSummary } from "../sections/general";

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuthContext } from "../auth/useAuthContext";
import {
  fetchLineGraph,
  fetchPieGraph,
  fetchRevenueGraph,
} from "../redux/slices/analytics";
import { fetchEnquiryCharts } from "../redux/slices/enquiry-chart";
import { fetchOrders } from "../redux/slices/order";
import { fetchProducts } from "../redux/slices/product";
import { fetchDashboardData } from "../redux/slices/dashboard";
// assets
// ----------------------------------------------------------------------

export default function LandingPage() {
  const { logout, user } = useAuthContext();

  const products = useSelector((state) => state.product.products);
  const orders = useSelector((state) => state.order.orders);

  const line = useSelector((state) => state.analytics.line);
  const revenueLine = useSelector((state) => state.analytics.revenueLine);
  const dashboardData = useSelector(
    (state) => state.dashboardData.dashboardData,
  );

  const pie = useSelector((state) => state.analytics.pie);

  const [pendingOrders, setPendingOrders] = useState([]);

  const [lineMonth, setLineMonth] = useState(new Date().getMonth() + 1);
  const [lineYear, setLineYear] = useState(new Date().getFullYear());
  const [pieMonth, setPieMonth] = useState(new Date().getMonth() + 1);
  const [pieYear, setPieYear] = useState(new Date().getFullYear());

  const enquiryCharts = useSelector(
    (state) => state.enquiryChart.enquiryCharts,
  );

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const { themeStretch } = useSettingsContext();

  useEffect(() => {
    dispatch(fetchDashboardData({ enqueueSnackbar }));
  }, []);

  useEffect(() => {
    dispatch(fetchProducts({ enqueueSnackbar }));
  }, [dispatch, enqueueSnackbar]);

  useEffect(() => {
    dispatch(fetchEnquiryCharts({ enqueueSnackbar }));
  }, [dispatch, enqueueSnackbar]);

  useEffect(() => {
    dispatch(fetchOrders({ enqueueSnackbar }));
  }, [dispatch, enqueueSnackbar]);

  useEffect(() => {
    dispatch(
      fetchLineGraph({
        data: { month: lineMonth, year: lineYear },
        enqueueSnackbar,
      }),
    );
  }, [dispatch, enqueueSnackbar, lineMonth, lineYear]);

  useEffect(() => {
    dispatch(
      fetchRevenueGraph({
        data: { month: lineMonth, year: lineYear },
        enqueueSnackbar,
      }),
    );
  }, [dispatch, enqueueSnackbar, lineMonth, lineYear]);

  useEffect(() => {
    dispatch(
      fetchPieGraph({
        data: { month: pieMonth, year: pieYear },
        enqueueSnackbar,
      }),
    );
  }, [dispatch, enqueueSnackbar, pieMonth, pieYear]);

  useEffect(() => {
    if (orders) {
      const pending = orders?.data?.filter(
        (order) => order.status === "pending",
      );
      setPendingOrders(pending);
    }
  }, [orders]);

  const handleLogout = async () => {
    try {
      logout();
      navigate(PATH_AUTH.login, { replace: true });
    } catch (error) {
      enqueueSnackbar("Unable to logout!", { variant: "error" });
    }
  };

  // const chartData = [
  //   { name: "Jan", value: 400 },
  //   { name: "Feb", value: 300 },
  //   { name: "Mar", value: 600 },
  //   { name: "Apr", value: 400 },
  //   { name: "May", value: 700 },
  //   { name: "Jun", value: 900 },
  // ];

  const lineChartOptions = {
    chart: {
      id: "line-chart",
      toolbar: {
        show: false,
      },
    },
    title: {
      text: "Daily Order Distribution",
      align: "left",
      style: {
        fontSize: "20px",
        fontWeight: "bold",
        fontFamily: "CircularStd, sans-serif",
        color: "#00000080",
      },
    },
    xaxis: {
      categories: line?.data?.map((data) => {
        const date = new Date(data.week_start);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }),
    },
  };

  const revenueLineChartOptions = {
    chart: {
      id: "revenue-line-chart",
      toolbar: {
        show: false,
      },
    },
    title: {
      text: "Daily Revenue Distribution",
      align: "left",
      style: {
        fontSize: "20px",
        fontWeight: "bold",
        fontFamily: "CircularStd, sans-serif",
        color: "#00000080",
      },
    },
    xaxis: {
      categories: revenueLine?.data?.map((data) => {
        const date = new Date(data.week_start);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }),
    },
  };

  // const barChartOptions = {
  //   chart: {
  //     id: "bar-chart",
  //     toolbar: {
  //       show: false,
  //     },
  //   },
  //   xaxis: {
  //     categories: chartData.map((data) => data.name),
  //   },
  // };

  // const areaChartOptions = {
  //   chart: {
  //     id: "area-chart",
  //     toolbar: {
  //       show: false,
  //     },
  //   },
  //   xaxis: {
  //     categories: chartData.map((data) => data.name),
  //   },
  // };

  const pieChartSeries =
    pie?.data?.map((data) => parseInt(data.total_sold)) || [];

  const pieChartOptions = {
    title: {
      text: "Product Distribution",
      align: "left",
      style: {
        fontSize: "20px",
        fontWeight: "bold",
        fontFamily: "CircularStd, sans-serif",
        color: "#00000080",
      },
    },
    labels: pie?.data?.map((data) => data?.name || ""),
  };

  // Enquiry sources chart data
  const enquiryChartSeries =
    enquiryCharts?.data?.map((item) => item.total) || [];
  const enquiryChartOptions = {
    title: {
      text: "Enquiry Sources",
      align: "left",
      style: {
        fontSize: "20px",
        fontWeight: "bold",
        fontFamily: "CircularStd, sans-serif",
        color: "#00000080",
      },
    },
    labels: enquiryCharts?.data?.map((item) => item.enquiry_source) || [],
  };

  return (
    <>
      <Helmet>
        <title> Home | Pumpernickel Dashboard</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : "xl"}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ mb: 4 }} variant="h4" gutterBottom>
            Dashboard
          </Typography>

          <Button
            variant="contained"
            onClick={handleLogout}
            style={{ cursor: "pointer", marginTop: 35, marginBottom: 35 }}
            startIcon={<Iconify icon="eva:log-out-outline" />}
          >
            Logout
          </Button>
        </div>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <AppWidgetSummary
              link={PATH_DASHBOARD.products.list}
              title="Total Products"
              total={products?.meta?.total}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <AppWidgetSummary
              link={`${PATH_DASHBOARD.orders.list}?status=pending`}
              title="Pending Orders"
              total={pendingOrders?.length}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <AppWidgetSummary
              link={`${PATH_DASHBOARD.orders.list}`}
              title="Total Orders"
              total={orders?.meta?.total}
            />
          </Grid>
          {user?.role?.slug === "admin" && (
            <Grid item xs={12} md={3}>
              <AppWidgetSummary
                link={`${PATH_DASHBOARD.orders.list}?status=completed`}
                title="Total Completed Revenue"
                total={dashboardData?.data?.totalCompletedRevenue}
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={lineMonth}
                onChange={(e) => setLineMonth(e.target.value)}
                label="Month"
              >
                {Array.from({ length: 12 }, (_, index) => (
                  <MenuItem key={index} value={index + 1}>
                    {new Date(0, index).toLocaleString("en-US", {
                      month: "long",
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={lineYear}
                onChange={(e) => setLineYear(e.target.value)}
                label="Year"
              >
                {Array.from({ length: 10 }, (_, index) => (
                  <MenuItem
                    key={index}
                    value={new Date().getFullYear() - index}
                  >
                    {new Date().getFullYear() - index}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {line && line?.data?.length > 0 && (
            <Grid item xs={12} md={12}>
              <Card>
                <CardContent>
                  <Chart
                    options={lineChartOptions}
                    series={[
                      { data: line?.data?.map((data) => data.total_orders) },
                    ]}
                    type="line"
                    width="100%"
                  />
                </CardContent>
              </Card>
            </Grid>
          )}
          {user?.role?.slug === "admin" &&
            revenueLine &&
            revenueLine?.data?.length > 0 && (
              <Grid item xs={12} md={12}>
                <Card>
                  <CardContent>
                    <Chart
                      options={revenueLineChartOptions}
                      series={[
                        {
                          data: revenueLine?.data?.map(
                            (data) => data.total_revenue,
                          ),
                        },
                      ]}
                      type="line"
                      width="100%"
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
          {/* <Grid
            item
            xs={6}
            md={6}
          >
            <Card>
              <CardContent>
                <Chart
                  options={barChartOptions}
                  series={[{ data: chartData.map((data) => data.value) }]}
                  type="bar"
                  width="100%"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={6}
            md={6}
          >
            <Card>
              <CardContent>
                <Chart
                  options={areaChartOptions}
                  series={[{ data: chartData.map((data) => data.value) }]}
                  type="area"
                  width="100%"
                />
              </CardContent>
            </Card>
          </Grid> */}

          {pie && pie?.data?.length > 0 && (
            <Grid item xs={6} md={6}>
              <Card>
                <CardContent>
                  <Chart
                    options={pieChartOptions}
                    series={pieChartSeries}
                    type="pie"
                    width="100%"
                  />
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Enquiry Sources Chart */}
          {enquiryCharts?.data?.length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Chart
                    options={enquiryChartOptions}
                    series={enquiryChartSeries}
                    type="pie"
                    width="100%"
                  />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
}
